import React, { useState, useEffect } from 'react';
import { Store, Plus, Trash2, CheckCircle2, AlertTriangle, Layers, Tag, X } from 'lucide-react';
import { storeAPI } from '../utils/api';

const StoreManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("Frame");
  
  // As per mobile app fixed categories
  const categories = ["Frame", "Car", "Chat Bubbles", "Gifts", "Customise I'd"];

  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    category: 'Frame',
    price: '',
    level: '1',
    image_url: '',
    duration_days: '30'
  });

  useEffect(() => {
    fetchItems();
  }, [activeCategory]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await storeAPI.getItems(activeCategory);
      if (res.data.success) {
        setItems(res.data.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    setModalError('');
    try {
      const res = await storeAPI.createItem({
        ...formData,
        price: Number(formData.price),
        level: Number(formData.level),
        duration_days: Number(formData.duration_days)
      });
      if (res.data.success) {
        setModalOpen(false);
        setFormData({ title: '', category: 'Frame', price: '', level: '1', image_url: '', duration_days: '30' });
        fetchItems();
      }
    } catch (err) {
      setModalError(err.response?.data?.message || "Failed to create item");
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this store item?")) return;
    try {
      const res = await storeAPI.deleteItem(id);
      if (res.data.success) {
        fetchItems();
      }
    } catch (err) {
      alert("Failed to delete item");
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Virtual Store Assets</h2>
          <p className="text-slate-500 mt-1">Manage frames, cars, chat bubbles, and custom IDs available in the store.</p>
        </div>
        <button 
          onClick={() => { setModalOpen(true); setFormData({ ...formData, category: activeCategory }); }}
          className="px-5 py-2.5 bg-pink-600 hover:bg-pink-700 active:scale-95 text-white font-bold rounded-xl transition flex items-center gap-2 shadow-lg shadow-pink-200"
        >
          <Plus size={18} /> Add New Asset
        </button>
      </div>

      {/* Category Tabs */}
      <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-thin">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-6 py-2.5 rounded-full font-bold text-sm whitespace-nowrap transition-all ${
              activeCategory === cat 
                ? 'bg-slate-800 text-white shadow-md' 
                : 'bg-white text-slate-500 hover:bg-slate-50 border border-slate-200'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center p-10"><div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full"></div></div>
      ) : items.length === 0 ? (
        <div className="bg-white p-16 rounded-3xl border border-slate-100 flex flex-col items-center justify-center text-slate-400">
          <Store size={48} className="opacity-20 mb-3" />
          <p className="font-bold">No assets found in {activeCategory}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
          {items.map(item => (
            <div key={item.id} className="bg-slate-900 rounded-3xl overflow-hidden flex flex-col relative group">
              {/* Asset Preview */}
              <div className="h-32 p-4 flex items-center justify-center relative">
                <div className="absolute top-2 left-2 bg-pink-600 px-2 py-0.5 rounded-full text-[10px] font-bold text-white shadow-md">
                  Lv{item.level}
                </div>
                {item.image_url ? (
                  <img src={item.image_url} alt={item.title} className="max-h-full object-contain drop-shadow-2xl" />
                ) : (
                  <Layers size={40} className="text-slate-600" />
                )}
                
                {/* Delete Overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-sm">
                  <button onClick={() => handleDelete(item.id)} className="p-3 bg-red-500 text-white rounded-full hover:scale-110 active:scale-95 transition shadow-lg">
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
              
              {/* Asset Details */}
              <div className="bg-white p-4 flex-1 flex flex-col justify-between border-t border-slate-100">
                <div>
                  <h4 className="font-bold text-slate-800 text-xs truncate" title={item.title}>{item.title}</h4>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-amber-500 font-extrabold text-sm flex items-center gap-1">
                    🪙 {item.price}
                  </span>
                  <span className="text-[10px] text-slate-400 font-bold bg-slate-50 px-2 py-1 rounded-md">{item.duration_days}d</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in duration-200">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2"><Plus size={20} className="text-pink-600" /> Add New {formData.category}</h3>
              <button onClick={() => setModalOpen(false)} className="p-1.5 text-slate-400 hover:bg-slate-200 rounded-full transition"><X size={18} /></button>
            </div>

            <form onSubmit={handleCreate} className="p-6 space-y-4">
              {modalError && (
                <div className="p-3 bg-red-50 text-red-600 border border-red-100 rounded-xl text-xs font-bold flex items-center gap-2">
                  <AlertTriangle size={14} /> {modalError}
                </div>
              )}

              <div>
                <label className="text-xs font-bold text-slate-400 uppercase ml-1">Asset Category</label>
                <select
                  required
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 font-semibold text-slate-700 mt-1"
                  value={formData.category}
                  onChange={e => setFormData({ ...formData, category: e.target.value })}
                >
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 uppercase ml-1">Asset Name</label>
                <input
                  required
                  placeholder="e.g. Honor Frame"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 font-semibold text-slate-700 mt-1"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase ml-1">Price (Coins)</label>
                  <input
                    required
                    type="number"
                    placeholder="12000"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-amber-500/20 font-bold text-amber-600 mt-1"
                    value={formData.price}
                    onChange={e => setFormData({ ...formData, price: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase ml-1">Req Level</label>
                  <input
                    required
                    type="number"
                    placeholder="1"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 font-semibold text-slate-700 mt-1"
                    value={formData.level}
                    onChange={e => setFormData({ ...formData, level: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-xs font-bold text-slate-400 uppercase ml-1">Image URL</label>
                  <input
                    placeholder="https://...png"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 font-semibold text-slate-700 mt-1"
                    value={formData.image_url}
                    onChange={e => setFormData({ ...formData, image_url: e.target.value })}
                  />
                </div>
                {/* <div>
                  <label className="text-xs font-bold text-slate-400 uppercase ml-1">Duration (Days)</label>
                  <input
                    required
                    type="number"
                    placeholder="30"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 font-semibold text-slate-700 mt-1"
                    value={formData.duration_days}
                    onChange={e => setFormData({ ...formData, duration_days: e.target.value })}
                  />
                </div> */}
              </div>

              <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
                <button type="button" onClick={() => setModalOpen(false)} className="px-5 py-2.5 font-bold text-slate-500 hover:bg-slate-100 rounded-xl">Cancel</button>
                <button type="submit" disabled={modalLoading} className="px-5 py-2.5 font-bold bg-pink-600 text-white rounded-xl shadow-lg shadow-pink-200">
                  {modalLoading ? "Saving..." : "Publish Asset"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreManagement;
